import { getGlobalHeadersAsObject } from "../global-headers";

export type Person = {
	id: number;
	name: string;
	country_region: string;
	/** Can contain HTML markup when highlighted (e.g. <mark>text</mark>) */
	summary: string;
	extra_info: string;
	perdiction_set: Array<any>;
	user_hash: string;
	source?: number;
	last_extracted: string | null;
	url: string;
	/** Can contain HTML markup when highlighted (e.g. <mark>text</mark>) */
	headline: string;
	unavailable: boolean;
	open_to_work?: boolean;
	location_id: number | null;
	ostmd_id: number | null;
	created: string;
	updated: string;
	vanity_name: string | null;
};

export type PersonText = {
	text: string;
};

export type PersonSummary = {
	id: number;
	name: string;
	llm_summary: string;
	url: string;
};

export class PersonNotFoundError extends Error {
	status: number;
	constructor(id: number) {
		super(`Person ${id} not found`);
		this.name = "PersonNotFoundError";
		this.status = 404;
	}
}

export const getPersonById = (id: number, assignmentId?: number): Promise<Person> => {
	if (!id || typeof id !== "number") {
		throw new Error(
			`getPersonById called with invalid argument id '${id}'. Expected number`
		);
	}

	const url = new URL(`/api/v2/persons/${id}/`, window.location.origin);
	if (assignmentId) {
		url.searchParams.append("highlight", assignmentId.toString());
	}

	return fetch(url.toString(), {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => {
		if (!res.ok) {
			if (res.status === 404) {
				throw new PersonNotFoundError(id);
			}
			throw new Error(`Failed to fetch person ${id}: ${res.status}`);
		}
		return res.json();
	});
};

export const getPersonSummaryById = (
	id: number,
	words: number = 20
): Promise<PersonSummary> => {
	if (!id || typeof id !== "number") {
		throw new Error(
			`getPersonSummaryById called with invalid argument id '${id}'. Expected number`
		);
	}

	const url = `/api/v2/persons/${id}/llm_summary/?words=${words}`;
	return fetch(url, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => {
		if (!res.ok) {
			throw new Error(`Error fetching person summary: ${res.statusText}`);
		}
		return res.json();
	});
};

export const getPersonTextById = (id: number): Promise<PersonText> => {
	if (!id || typeof id !== "number") {
		throw new Error(
			`getPersonTextById called with invalid argument id '${id}'. Expected number`
		);
	}

	return fetch(`/api/v2/persons/${id}/text/`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => {
		if (!res.ok) {
			throw new Error(`Error fetching person text: ${res.statusText}`);
		}
		return res.json();
	});
};

type NullableString = string | null | undefined;

export interface CacheRecordBase {
	id: number;
	created: string;
	updated?: NullableString;
}

export interface DropcontactCacheRecord extends CacheRecordBase {
	request_id?: NullableString;
	linkedin_url?: NullableString;
	json?: unknown;
}

export interface SnovCacheRecord extends CacheRecordBase {
	linkedin_url?: NullableString;
	json?: unknown;
}

export interface RocketreachCacheRecord extends CacheRecordBase {
	linkedin_url?: NullableString;
	json?: unknown;
}

export interface ChannelResolveJobRecord extends CacheRecordBase {
	resolver: number;
	status: NullableString;
}

export type PersonDataSources = {
	dropcontact: DropcontactCacheRecord[];
	snov: SnovCacheRecord[];
	rocketreach: RocketreachCacheRecord[];
	channel_resolve_jobs: ChannelResolveJobRecord[];
};

/** Superusers only: every cached provider row for the person, newest first. */
export const getPersonDataSources = (id: number): Promise<PersonDataSources> => {
	return fetch(`/api/v2/persons/${id}/data_sources/`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => {
		if (!res.ok) {
			throw new Error(`Error fetching person data sources: ${res.statusText}`);
		}
		return res.json();
	});
};

export const putPerson = (person: Person): Promise<Person> => {
	return fetch(`/api/v2/persons/${person.id}/`, {
		method: "PUT",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify(person),
	}).then((res) => res.json());
};

export const getPersons = (tags: string[]) => {
	if (!tags || tags.length < 1) {
		throw new Error(
			`getPersons called with invalid argument tags '${tags}'. Expected non-empty list of strings`
		);
	}

	const requestUrl = new URL("/api/v2/persons/");
	const queryParams = [];

	queryParams.push(["tags", tags.join(",")]);

	return fetch(
		`${requestUrl.toString()}?${new URLSearchParams(queryParams).toString()}`,
		{
			method: "GET",
			headers: getGlobalHeadersAsObject(),
		}
	).then((res) => res.json());
};

export const extractPerson = (personId: number): Promise<Person> => {
	if (!personId || typeof personId !== "number") {
		throw new Error(
			`extractPerson called with invalid argument personId '${personId}'. Expected number`
		);
	}

	return fetch(`/api/v2/persons/${personId}/extract/`, {
		method: "POST",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => {
		if (!res.ok) {
			throw new Error(`Error extracting person: ${res.statusText}`);
		}
		return res.json();
	});
};

export const dataminePerson = (personId: number): Promise<Person> => {
	if (!personId || typeof personId !== "number") {
		throw new Error(
			`dataminePerson called with invalid argument personId '${personId}'. Expected number`
		);
	}

	return fetch(`/api/v2/persons/${personId}/datamine/`, {
		method: "POST",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => {
		if (!res.ok) {
			throw new Error(`Error datamining person: ${res.statusText}`);
		}
		return res.json();
	});
};

export type DeletePersonResponse = {
	deleted: boolean;
	id: number;
	name: string;
};

export const deletePerson = (personId: number): Promise<DeletePersonResponse> => {
	if (!personId || typeof personId !== "number") {
		throw new Error(
			`deletePerson called with invalid argument personId '${personId}'. Expected number`
		);
	}

	return fetch(`/api/v2/persons/${personId}/delete_person/`, {
		method: "DELETE",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => {
		if (!res.ok) {
			throw new Error(`Error deleting person: ${res.statusText}`);
		}
		return res.json();
	});
};

export type PersonPatchData = {
	name?: string;
	vanity_name?: string | null;
};

export const patchPerson = (
	personId: number,
	data: PersonPatchData
): Promise<Person> => {
	if (!personId || typeof personId !== "number") {
		throw new Error(
			`patchPerson called with invalid argument personId '${personId}'. Expected number`
		);
	}

	return fetch(`/api/v2/persons/${personId}/`, {
		method: "PATCH",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify(data),
	}).then((res) => {
		if (!res.ok) {
			throw new Error(`Error updating person: ${res.statusText}`);
		}
		return res.json();
	});
};

export type CandidateRole = {
	company: string;
	title: string;
	location: string;
	/** "YYYY-MM" or "YYYY"; empty when unknown */
	start_date: string;
	/** empty when the role is current */
	end_date: string;
	current: boolean;
	description: string;
};

export type CandidateEducation = {
	school_name: string;
	degree: string;
	field: string;
	start_year: number | null;
	end_year: number | null;
};

export type CandidateDraft = {
	name: string;
	headline: string;
	country_region: string;
	summary: string;
	emails: string[];
	phones: string[];
	skills: string[];
	roles: CandidateRole[];
	educations: CandidateEducation[];
};

/** An existing person the parsed CV might be — with what matched and a score. */
export type PersonMatch = {
	person_id: number;
	name: string;
	headline: string | null;
	url: string | null;
	score: number;
	confident: boolean;
	matched_on: string[];
};

export type CvParseResult = {
	parse_id: number;
	draft: CandidateDraft;
	matches: PersonMatch[];
};

/** Parse an uploaded CV (PDF/DOCX/TXT): stores it, returns draft + matches. */
export const parseCv = (file: File): Promise<CvParseResult> => {
	const data = new FormData();
	data.append("file", file);

	return fetch("/api/v2/persons/parse_cv/", {
		method: "POST",
		headers: getGlobalHeadersAsObject(),
		body: data,
	}).then(async (res) => {
		if (!res.ok) {
			const body = await res.json().catch(() => ({}));
			throw new Error(body.error || `Failed to parse CV (${res.status})`);
		}
		return res.json();
	});
};

/** Attach a parsed CV to an existing person (user confirmed the match). */
export const associateCv = (
	parseId: number,
	personId: number,
	matchedBy?: string
): Promise<Person> => {
	return fetch("/api/v2/persons/associate_cv/", {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			parse_id: parseId,
			person_id: personId,
			matched_by: matchedBy,
		}),
	}).then(async (res) => {
		if (!res.ok) {
			const body = await res.json().catch(() => ({}));
			throw new Error(body.error || `Failed to associate CV (${res.status})`);
		}
		return res.json();
	});
};

/** Create a Person (+ employer/role/skills/contact) for a non-LinkedIn candidate.
 * Pass parseId to also attach the uploaded CV to the new person. */
export const createCandidate = (
	draft: CandidateDraft,
	parseId?: number
): Promise<Person> => {
	// Sanitize the string-list fields before POST. The backend validates each
	// element, so a blank row or LLM-extracted junk (e.g. a template CV yielding
	// "professional email address") would 400. Emails must look like an address;
	// drop anything that doesn't rather than block the whole create.
	const isEmail = (e: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
	const cleaned: CandidateDraft = {
		...draft,
		emails: draft.emails.map((e) => e.trim()).filter(isEmail),
		phones: draft.phones.map((p) => p.trim()).filter(Boolean),
		skills: draft.skills.map((s) => s.trim()).filter(Boolean),
	};
	return fetch("/api/v2/persons/create_candidate/", {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify(parseId ? { ...cleaned, parse_id: parseId } : cleaned),
	}).then(async (res) => {
		if (!res.ok) {
			const body = await res.json().catch(() => ({}));
			throw new Error(body.error || `Failed to create candidate (${res.status})`);
		}
		return res.json();
	});
};

export type IsConnectedResponse = {
	is_connected: boolean;
};

export const getIsConnected = (personId: number): Promise<IsConnectedResponse> => {
	if (!personId || typeof personId !== "number") {
		throw new Error(
			`getIsConnected called with invalid argument personId '${personId}'. Expected number`
		);
	}

	return fetch(`/api/v2/persons/${personId}/is_connected/`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => {
		if (!res.ok) {
			throw new Error(`Error checking connection: ${res.statusText}`);
		}
		return res.json();
	});
};
